import { useEffect, useState, useCallback } from 'react';
import { api } from '../api.js';
import { IN_PIPELINE_STATUSES } from '@hunter/shared/applications';

// T079 — Sidebar-driven counts. Pulls /recommendations/stats once on
// mount + every 60s, plus on demand when the operator triggers an action
// that would change a count (we expose `refresh` for that).
//
// Returns counts the sidebar needs:
//   - newCount        — "new" status (dashboard "X new" badge)
//   - inFlightCount   — applications currently in screening/interview/offer
//   - unlinkedMail    — replies the matcher would not attach on its own (T360)
//                       (the most "active" pipeline states — the ones that
//                       benefit from at-a-glance count signaling)
//   - unreadDigests   — T221; morning digests he has not opened yet
//
// Cross-component sync: a module-level subscription set means firing
// `refresh()` from one mounted instance updates every other instance
// without a Context wrapper.

const POLL_MS = 60_000;
const _listeners = new Set();
// T221 — `unreadDigests` must appear HERE as well as in `_fetch`. A badgeKey
// the sidebar reads but the initial cache does not define renders 0 forever on
// first paint and looks like "nothing to see" rather than "not loaded yet".
let _cache = { newCount: 0, inFlightCount: 0, unreadDigests: 0, unlinkedMail: 0, openTasks: 0, loaded: false };

// T290b — a request in flight when the account ends does not know that. It
// resolves afterwards and writes the PREVIOUS account's counts back over the
// cleared cache, with `loaded: true`, so the sidebar re-arms with numbers that
// belong to someone else and nothing refetches until the next poll. The reset
// bumps this; a fetch that started under an older epoch throws its answer away.
let _epoch = 0;

async function _fetch() {
  const epoch = _epoch;
  try {
    // T221 — two endpoints, one paint. `Promise.allSettled` rather than `all`:
    // the digest count is a nicety and must never be able to blank the
    // recommendation badges the operator actually navigates by.
    const [statsRes, digestRes, mailRes] = await Promise.allSettled([
      api.get('/recommendations/stats'),
      api.get('/digests?limit=1'),
      // T360 — same reasoning as the digest count: a nicety that must never be
      // able to blank the badges he navigates by.
      api.get('/emails/counts'),
    ]);
    if (statsRes.status === 'rejected') throw statsRes.reason;
    const body = statsRes.value;
    const bs = body?.byStatus ?? {};
    if (epoch !== _epoch) return; // the account changed while this was in flight
    _cache = {
      newCount: bs.new ?? 0,
      // T271(1) — this hand-summed three statuses and omitted `applied`, the
      // largest group. Measured 2026-09-04: the badge said 2 while the tracker
      // header, counting the same applications, said 48.
      inFlightCount: [...IN_PIPELINE_STATUSES].reduce((n, k) => n + (bs[k] ?? 0), 0),
      unreadDigests: digestRes.status === 'fulfilled' ? (digestRes.value?.unread ?? 0) : 0,
      unlinkedMail: mailRes.status === 'fulfilled' ? (mailRes.value?.unmatched ?? 0) : 0,
      // T376 — stage changes awaiting his word. Scheduled events are not
      // counted here: a badge you cannot take to zero teaches you to ignore it,
      // and an interview next Tuesday is not an outstanding task today.
      // T413 — every kind of work the Tasks page shows, summed on the
      // server from the same queries the page's sections use. It counted
      // proposals alone, so the page could be full and the badge say
      // nothing.
      openTasks: mailRes.status === 'fulfilled' ? (mailRes.value?.openTasks ?? 0) : 0,
      loaded: true,
    };
    _listeners.forEach((fn) => fn(_cache));
  } catch {
    // Don't surface — sidebar gracefully renders 0s. The stats endpoint
    // failing isn't worth a toast (the operator's main flow is unaffected).
  }
}

export function useNavCounts() {
  const [counts, setCounts] = useState(_cache);

  useEffect(() => {
    _listeners.add(setCounts);
    if (!_cache.loaded) _fetch();
    // T438 — do not poll a tab nobody is looking at.
    //
    // Three requests a minute, forever, whether the window is in front or
    // buried behind nine others since yesterday. Nothing here changes
    // without a scan or an action, and both of those already push through
    // `refreshNavCounts()`, so a hidden tab's polling buys nothing and
    // costs a round trip a minute per open tab.
    //
    // On becoming visible it fetches ONCE immediately rather than waiting
    // out the interval: coming back to a stale badge is the whole thing
    // the poll exists to prevent.
    const tick = () => {
      if (document.visibilityState === 'hidden') return;
      _fetch();
    };
    const t = setInterval(tick, POLL_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible') _fetch();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      _listeners.delete(setCounts);
      clearInterval(t);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  const refresh = useCallback(() => _fetch(), []);
  return { ...counts, refresh };
}

// Hook-free imperative refresh for callers outside React (e.g. action
// handlers in event listeners).
// T290 — see resetActivityCache(). Same module-scope cache, same logout with no
// reload: the sidebar badges would carry the previous account's counts into the
// next session until the first poll returned.
export function resetNavCounts() {
  _epoch += 1;
  _cache = { newCount: 0, inFlightCount: 0, unreadDigests: 0, unlinkedMail: 0, openTasks: 0, loaded: false };
  _listeners.forEach((fn) => fn(_cache));
}

export function refreshNavCounts() {
  return _fetch();
}
